import { Chord, Id, Section } from '../types';
import { RootState } from './store';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

const initialState: Section[] = [];

export const sectionsSlice = createSlice({
	name: 'sections',
	initialState,
	reducers: {
		addSection: (state, action: PayloadAction<Section>) => {
			state.push(action.payload);
		},
		loadSections: (_, action: PayloadAction<Section[]>) => action.payload,
		removeSection: (state, action: PayloadAction<Id>) => {
			state = state.filter(section => section.id !== action.payload);

			return state;
		},
		updateSection: (state, action: PayloadAction<Partial<Section>>) => {
			// console.log({ ap: action.payload });
			state = state.map(section =>
				section.id === action.payload.id ? { ...section, ...action.payload } : section
			);

			return state;
		},
		addChord: (
			state,
			action: PayloadAction<{
				sectionId: Id;
				chord: Omit<Chord, 'parentSectionId'>;
			}>
		) => {
			const { sectionId, chord } = action.payload;
			const section = state.find(section => section.id === sectionId);
			if (section) {
				section.chords.push({ ...chord, parentSectionId: sectionId });
			}
		},
		removeChord: (state, action: PayloadAction<{ sectionId: Id; chordId: Id }>) => {
			const { sectionId, chordId } = action.payload;
			const section = state.find(section => section.id === sectionId);
			if (section) {
				section.chords = section.chords.filter(chord => chord.id !== chordId);
			}
		},
		updateChord: (
			state,
			action: PayloadAction<{
				sectionId: Id;
				chord: Omit<Chord, 'parentSectionId'>;
			}>
		) => {
			const { sectionId, chord } = action.payload;
			const section = state.find(section => section.id === sectionId);
			if (section) {
				section.chords = section.chords.map(oldChord =>
					oldChord.id === chord.id ? { ...chord, parentSectionId: sectionId } : oldChord
				);
			}
		},
		swapChords: (
			state,
			action: PayloadAction<{
				sectionId: Id;
				chordOneId: Id | null;
				chordTwoId: Id | null;
			}>
		) => {
			const { sectionId, chordOneId, chordTwoId } = action.payload;
			if (chordOneId == null || chordTwoId == null) return;
			const section = state.find(section => section.id === sectionId);
			if (section) {
				const chordOneIndex = section.chords.findIndex(chord => chord.id === chordOneId);
				const chordTwoIndex = section.chords.findIndex(chord => chord.id === chordTwoId);
				[section.chords[chordOneIndex], section.chords[chordTwoIndex]] = [
					section.chords[chordTwoIndex],
					section.chords[chordOneIndex]
				];
			}
		},
		clearAll: () => {
			return initialState;
		}
	}
});

export const {
	addSection,
	loadSections,
	removeSection,
	updateSection,
	addChord,
	removeChord,
	updateChord,
	swapChords,
	clearAll
} = sectionsSlice.actions;

export default sectionsSlice.reducer;

const getSectionById = (id: Id, sections: Section[]) => sections.find(section => section.id === id);

export const selectSections = (state: RootState) => state.sections;
export const selectEnabledSections = (state: RootState) =>
	state.sections.filter(section => section.enabled);
export const selectChordIndexById = (chordId: Id, parentSectionId: Id) => (state: RootState) => {
	const parentSection = getSectionById(parentSectionId, state.sections);
	return parentSection?.chords.findIndex(chord => chord.id === chordId);
};
export const selectSectionById = (id: Id) => (state: RootState) =>
	getSectionById(id, state.sections);
export const selectIsFirstChord = (chordId: Id, parentSectionId: Id) => (state: RootState) => {
	const parentSection = getSectionById(parentSectionId, state.sections);
	const chordIndex = parentSection?.chords.findIndex(chord => chord.id === chordId);
	return chordIndex === 0;
};
export const selectIsLastChord = (chordId: Id, parentSectionId: Id) => (state: RootState) => {
	const parentSection = getSectionById(parentSectionId, state.sections);
	const chordIndex = parentSection?.chords.findIndex(chord => chord.id === chordId);
	return parentSection?.chords.length != null && chordIndex === parentSection?.chords.length - 1;
};
export const selectSectionIndexById = (id: Id) => (state: RootState) =>
	state.sections.findIndex(section => section.id === id);
